/* eslint-disable no-unused-vars */
import { ReactNode, useEffect, useRef, useState } from "react";
import parse from "html-react-parser";
import { commonHtmlParserOptions } from "@utils/html-parser";
import { twMerge } from "tailwind-merge";
import classNames from "classnames";
import AltArrowDown from "@svg/new-design/alt-arrow-down";
import { ICON_SIZE, IconButton } from "../icon-button";
import { processMathTags } from "@modules/katex/katexFormatter";
import { useIsMobile } from "@shared/hooks/useIsMobile";

interface AccordionProps {
  title: string | ReactNode;
  open?: boolean;
  hasResult?: boolean;
  className?: ((isOpen?: boolean) => string) | string;
  titleClassName?: ((isOpen?: boolean) => string) | string;
  contentClassName?: ((isOpen?: boolean) => string) | string;
  openAccordion?: (isOpen: boolean) => void;
  iconComponent?: (isOpen?: boolean) => React.ReactNode;
  titleIsHtml?: boolean;
  isLocked?: boolean;
  lockClickHandler?: () => void;
  children?: ReactNode;
}

const Accordion: React.FC<AccordionProps> = ({
  titleIsHtml,
  title,
  open,
  children,
  className = "",
  titleClassName = "",
  contentClassName = "",
  iconComponent,
  openAccordion = () => {},
  isLocked,
  lockClickHandler,
}) => {
  const [isOpen, setOpen] = useState<boolean>(open || false);
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile(600);

  useEffect(() => {
    const isReady = isOpen && contentRef.current;
    setContentHeight(
      isReady ? (contentRef.current?.scrollHeight ?? 0) + 24 : 0
    );
  }, [isOpen]);

  useEffect(() => {
    setOpen(Boolean(open));
  }, [open]);

  useEffect(() => {
    const el = contentRef?.current;
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries && entries.length > 0) {
        const { height } = entries[0].contentRect;

        setContentHeight(height + 24);
      }
    });

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      if (el) resizeObserver.unobserve(el);
      resizeObserver.disconnect();
    };
  }, []);

  const toggleAccordion = () => {
    if (isLocked) return lockClickHandler?.();
    openAccordion(!isOpen);
    setOpen(!isOpen);
  };

  const renderIconComponent = iconComponent ? (
    iconComponent(isOpen)
  ) : (
    <IconButton
      size={isMobile ? ICON_SIZE.SMALL : ICON_SIZE.MEDIUM}
      hideTooltip={true}
      iconClassName={classNames(
        "h-5 w-5 fill-none text-fds-icon-secondary transition-transform duration-300 ease-linear",
        { "rotate-180": isOpen }
      )}
      icon={<AltArrowDown />}
      aria-label="باز و بستن محتوا"
      className="grow shrink-0 max-w-8    fds-sm:max-w-10 "
    />
  );
  const wrapperClassNames =
    typeof className === "string" ? className : className(isOpen);
  const titleClassNames =
    typeof titleClassName === "string"
      ? titleClassName
      : titleClassName(isOpen);
  const contentClassNames =
    typeof contentClassName === "string"
      ? contentClassName
      : contentClassName(isOpen);

  return (
    <div
      className={classNames(
        "w-full rounded-8 border border-fds-default  overflow-hidden ",
        !isOpen ? "bg-fds-primary" : "bg-fds-canvas-primary"
      )}
    >
      <div
        onClick={toggleAccordion}
        className={twMerge(
          "flex justify-between items-center box-border px-5    py-4    ",
          isLocked && !lockClickHandler ? "cursor-auto" : "cursor-pointer",
          wrapperClassNames,
          isOpen ? "pb-2" : ""
        )}
      >
        <div
          className={twMerge(
            "fds-sm:title-lg  title-md-demibold flex-shrink overflow-auto break-words",
            titleClassNames
          )}
        >
          {titleIsHtml && typeof title === "string"
            ? parse(
                processMathTags(title || "") ?? "",
                commonHtmlParserOptions()
              )
            : title}
        </div>
        {renderIconComponent}
      </div>
      <div
        className={twMerge(
          "w-full overflow- body-md text-fds-secondary  transition-[max-height] duration-200 ease-linear",
          contentClassNames,
          isOpen ? "pb-4      px-5    pt-0" : ""
        )}
        style={{ maxHeight: isOpen ? contentHeight : 0 }}
      >
        <div ref={contentRef} className="w-full flex flex-col">
          {isOpen && children}
        </div>
      </div>
    </div>
  );
};

export default Accordion;
